import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { 
  Wallet, DollarSign, TrendingUp, Shield, ExternalLink,
  PieChart, BarChart3, ArrowUp, ArrowDown, RefreshCw, Lock,
  Coins, Building, Globe
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Treasury = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [treasuryData, setTreasuryData] = useState({
    totalValue: 2500000,
    btcHoldings: 42.5,
    btcValue: 1625000,
    ethHoldings: 850,
    ethValue: 625000,
    stableHoldings: 250000,
    change24h: 3.2,
  });

  const [walletAddresses] = useState({
    btc: 'bc1qn4nf4jkz8qv95emhezyk6n5x2qgpclxrcg0s4y',
    eth: '0xb745dc81c09bb00cc2faec09aa9a68dd131bace2',
  });

  const assetBreakdown = [
    { name: 'Bitcoin (BTC)', value: treasuryData.btcValue, percentage: 65, color: '#F7931A', icon: '₿' },
    { name: 'Ethereum (ETH)', value: treasuryData.ethValue, percentage: 25, color: '#627EEA', icon: 'Ξ' },
    { name: 'Stablecoins', value: treasuryData.stableHoldings, percentage: 10, color: '#26A17B', icon: '$' },
  ];

  const recentTransactions = [
    { type: 'inflow', asset: 'BTC', amount: '2.5', value: '$95,750', time: '2 hours ago' },
    { type: 'outflow', asset: 'ETH', amount: '50', value: '$92,500', time: '5 hours ago' },
    { type: 'inflow', asset: 'USDT', amount: '25,000', value: '$25,000', time: '1 day ago' },
    { type: 'inflow', asset: 'BTC', amount: '1.8', value: '$68,940', time: '2 days ago' },
  ];

  useEffect(() => {
    // Simulate loading
    setTimeout(() => setIsLoading(false), 1000);
  }, []);

  const StatCard = ({ title, value, subvalue, icon: Icon, color = 'text-[#D4AF37]', change }) => (
    <div className="glass-panel p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-white/60 text-sm">{title}</span>
        <Icon className={color} size={18} />
      </div>
      <p className="font-unbounded text-2xl font-bold text-white mb-1">{value}</p>
      {subvalue && <p className="text-sm text-white/60">{subvalue}</p>}
      {change !== undefined && (
        <div className={`flex items-center gap-1 text-sm mt-2 ${change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
          {change >= 0 ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
          <span>{Math.abs(change)}% (24h)</span>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen pt-20 pb-12 px-6" data-testid="treasury-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="font-unbounded text-4xl font-bold text-white mb-2">
              Platform <span className="gradient-text">Treasury</span>
            </h1>
            <p className="text-white/70">Transparent view of AIH ecosystem reserves</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2 glass-panel px-4 py-2">
              <Shield className="text-green-400" size={16} />
              <span className="text-green-400 text-sm">Verified</span>
            </div>
            <Button 
              variant="outline" 
              className="btn-secondary flex items-center gap-2"
              disabled={isLoading}
            >
              <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Total Treasury Value */}
        <div className="glass-panel p-8 mb-8 text-center" data-testid="total-treasury">
          <p className="text-white/60 mb-2">Total Treasury Value</p>
          <div className="flex items-center justify-center gap-4">
            <p className="font-unbounded text-5xl font-bold text-white">
              ${(treasuryData.totalValue / 1000000).toFixed(2)}M
            </p>
            <div className={`flex items-center gap-1 px-3 py-1 rounded-full ${treasuryData.change24h >= 0 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
              {treasuryData.change24h >= 0 ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
              <span>{Math.abs(treasuryData.change24h)}%</span>
            </div>
          </div>
          <p className="text-white/40 text-sm mt-2">Last updated: Just now</p>
        </div>

        {/* Asset Holdings */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <StatCard 
            title="Bitcoin Holdings" 
            value={`${treasuryData.btcHoldings} BTC`}
            subvalue={`$${(treasuryData.btcValue / 1000).toFixed(0)}K`}
            icon={Coins}
            color="text-[#F7931A]"
            change={4.2}
          />
          <StatCard 
            title="Ethereum Holdings" 
            value={`${treasuryData.ethHoldings} ETH`}
            subvalue={`$${(treasuryData.ethValue / 1000).toFixed(0)}K`}
            icon={Coins}
            color="text-blue-400"
            change={2.8}
          />
          <StatCard 
            title="Stablecoin Reserve" 
            value={`$${(treasuryData.stableHoldings / 1000).toFixed(0)}K`}
            subvalue="USDT, USDC"
            icon={DollarSign}
            color="text-green-400"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Asset Breakdown */}
          <div className="lg:col-span-2 space-y-6">
            {/* Asset Distribution */}
            <div className="glass-panel p-6" data-testid="asset-distribution">
              <h3 className="font-unbounded text-xl font-bold text-white mb-6">Asset Distribution</h3>
              
              <div className="space-y-4">
                {assetBreakdown.map((asset, i) => (
                  <div key={i} className="bg-[#0A1628]/80 rounded-xl p-4 border border-white/5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center text-xl"
                          style={{ backgroundColor: `${asset.color}20` }}
                        >
                          <span style={{ color: asset.color }}>{asset.icon}</span>
                        </div>
                        <div>
                          <p className="text-white font-medium">{asset.name}</p>
                          <p className="text-sm text-white/60">${(asset.value / 1000).toFixed(0)}K</p>
                        </div>
                      </div>
                      <span className="font-unbounded text-xl font-bold text-white">{asset.percentage}%</span>
                    </div>
                    <div className="h-2 bg-[#0A1628] rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all"
                        style={{ width: `${asset.percentage}%`, backgroundColor: asset.color }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Transactions */}
            <div className="glass-panel p-6" data-testid="recent-transactions">
              <h3 className="font-unbounded text-xl font-bold text-white mb-6">Recent Treasury Activity</h3>
              
              <div className="space-y-3">
                {recentTransactions.map((tx, i) => (
                  <div key={i} className="flex items-center justify-between py-3 border-b border-white/5 last:border-0">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${tx.type === 'inflow' ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                        {tx.type === 'inflow' ? (
                          <ArrowDown className="text-green-400" size={16} />
                        ) : (
                          <ArrowUp className="text-red-400" size={16} />
                        )}
                      </div>
                      <div>
                        <p className="text-white font-medium">
                          {tx.type === 'inflow' ? 'Received' : 'Sent'} {tx.amount} {tx.asset}
                        </p>
                        <p className="text-xs text-white/60">{tx.time}</p>
                      </div>
                    </div>
                    <span className={tx.type === 'inflow' ? 'text-green-400' : 'text-red-400'}>
                      {tx.type === 'inflow' ? '+' : '-'}{tx.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Wallet Addresses & Info */}
          <div className="space-y-6">
            {/* Treasury Wallets */}
            <div className="glass-panel p-6" data-testid="treasury-wallets">
              <h3 className="font-unbounded text-xl font-bold text-white mb-6">Treasury Wallets</h3>
              
              <div className="space-y-4">
                <div className="bg-[#0A1628]/80 rounded-xl p-4 border border-white/5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[#F7931A]">₿</span>
                    <span className="text-white font-medium">Bitcoin</span>
                  </div>
                  <p className="font-mono text-xs text-white/70 break-all mb-2">{walletAddresses.btc}</p>
                  <a 
                    href={`https://mempool.space/address/${walletAddresses.btc}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#D4AF37] text-sm flex items-center gap-1 hover:underline"
                  >
                    View on Explorer <ExternalLink size={12} />
                  </a>
                </div>

                <div className="bg-[#0A1628]/80 rounded-xl p-4 border border-white/5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-blue-400">Ξ</span>
                    <span className="text-white font-medium">Ethereum</span>
                  </div>
                  <p className="font-mono text-xs text-white/70 break-all mb-2">{walletAddresses.eth}</p>
                  <a 
                    href={`https://etherscan.io/address/${walletAddresses.eth}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#D4AF37] text-sm flex items-center gap-1 hover:underline"
                  >
                    View on Etherscan <ExternalLink size={12} />
                  </a>
                </div>
              </div>
            </div>

            {/* Treasury Strategy */}
            <div className="glass-panel p-6" data-testid="treasury-strategy">
              <h3 className="font-unbounded text-xl font-bold text-white mb-4">Treasury Strategy</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#F7931A]/20 flex items-center justify-center flex-shrink-0">
                    <Lock className="text-[#F7931A]" size={16} />
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">Bitcoin-Backed</p>
                    <p className="text-xs text-white/60">65% of treasury held in BTC for long-term value</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <Building className="text-blue-400" size={16} />
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">Multi-Sig Security</p>
                    <p className="text-xs text-white/60">3-of-5 multisig for all major transactions</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                    <Globe className="text-green-400" size={16} />
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">Full Transparency</p>
                    <p className="text-xs text-white/60">All wallets publicly verifiable on-chain</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="glass-panel p-6" data-testid="treasury-stats">
              <h3 className="font-unbounded text-lg font-bold text-white mb-4">Quick Stats</h3>
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-white/10">
                  <span className="text-white/60">BTC Price</span>
                  <span className="text-white font-medium">$38,235</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/10">
                  <span className="text-white/60">ETH Price</span>
                  <span className="text-white font-medium">$2,350</span>
                </div>
                <div className="flex justify-between py-2 border-b border-white/10">
                  <span className="text-white/60">AIH Backing Ratio</span>
                  <span className="text-green-400 font-medium">1.24x</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-white/60">Reserve Health</span>
                  <span className="text-green-400 font-medium">Healthy</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Treasury;
